// src/views/admin/caisse/ADCaisseConfig.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../../services/adminService";
import { Loader } from "../../../components/Loader";
import { useToastHook } from "../../../utils/useToast";
import { Modal, SubTabNav } from "./ADCaisseShared";

const SOUS_ONGLETS_CONFIG = [
  { key: "types-frais", label: "Types de frais", icon: "ti-receipt" },
  // { key: "types-eleves", label: "Types d'élèves", icon: "ti-user-star" },
  { key: "tranches", label: "Tranches", icon: "ti-calendar-stats" },
  { key: "affectations", label: "Affectations", icon: "ti-link" },
];

const ActionBtn = ({ title, icon, onClick, bg, color = "white" }) => (
  <button
    title={title}
    onClick={onClick}
    style={{ background: bg, color }}
    onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
    onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
  >
    <i className={`ti ${icon}`} />
  </button>
);

export default function ConfigCaisse({ slug }) {
  const [sousOnglet, setSousOnglet] = useState("types-frais");

  return (
    <div>
      <SubTabNav items={SOUS_ONGLETS_CONFIG} active={sousOnglet} onChange={setSousOnglet} style={{ marginBottom: 14 }} />

      {sousOnglet === "types-frais" && <TypesFrais slug={slug} />}
      {sousOnglet === "types-eleves" && <TypesEleves slug={slug} />}
      {sousOnglet === "tranches" && <Tranches slug={slug} />}
      {sousOnglet === "affectations" && <Affectations slug={slug} />}
    </div>
  );
}

/* ── Types de frais ─────────────────────────────────────────── */
const EMPTY_TF = { code: "", libelle: "", periode: "ANNUELLE", deductible_scolarite: false, actif: true };
const PERIODES = [
  { value: "ANNUELLE", label: "Annuelle" },
  { value: "TRIMESTRIELLE", label: "Trimestrielle" },
  { value: "MENSUELLE", label: "Mensuelle" },
  { value: "UNIQUE", label: "Unique" },
];

function TypesFrais({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_TF);

  const toast = useToastHook();
  const { getTypesFrais, createTypeFrais, updateTypeFrais, deleteTypeFrais } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getTypesFrais(slug));
    } catch (error) {
      toast.error("Impossible de charger les types de frais");
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(t =>
    t.libelle?.toLowerCase().includes(search.toLowerCase()) ||
    t.code?.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => { setForm(EMPTY_TF); setModal("create"); };
  const openEdit = (t) => { setSelected(t); setForm({ code: t.code, libelle: t.libelle, periode: t.periode, deductible_scolarite: t.deductible_scolarite, actif: t.actif }); setModal("edit"); };
  const openDelete = (t) => { setSelected(t); setModal("delete"); };
  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.code.trim() || !form.libelle.trim()) { toast.error("Le code et le libellé sont requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createTypeFrais(slug, form);
        setItems(prev => [...prev, created]);
        toast.success(`Type de frais "${created.libelle}" créé avec succès`);
      } else {
        const updated = await updateTypeFrais(slug, selected.id, form);
        setItems(prev => prev.map(t => t.id === selected.id ? updated : t));
        toast.success(`Type de frais "${updated.libelle}" modifié avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.code?.[0] || data?.libelle?.[0] || data?.non_field_errors?.[0] || "Une erreur est survenue";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteTypeFrais(slug, selected.id);
      setItems(prev => prev.filter(t => t.id !== selected.id));
      toast.success(`Type de frais "${selected.libelle}" supprimé avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer ce type de frais");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <input type="text" placeholder="Rechercher un type de frais…" value={search} onChange={e => setSearch(e.target.value)} />
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouveau type de frais</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Code</th><th>Libellé</th><th>Période</th><th>Déductible scolarité</th><th>Statut</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(t => (
              <tr key={t.id}>
                <td style={{ fontWeight: 500 }}>{t.code}</td>
                <td>{t.libelle}</td>
                <td>{PERIODES.find(p => p.value === t.periode)?.label || t.periode}</td>
                <td>{t.deductible_scolarite ? <span className="pill ok">Oui</span> : <span className="pill neu">Non</span>}</td>
                <td><span className={`pill ${t.actif ? "ok" : "warn"}`}>{t.actif ? "Actif" : "Inactif"}</span></td>
                <td>
                  <div className="act">
                    <ActionBtn title="Modifier" icon="ti-edit" onClick={() => openEdit(t)} bg="#ffc107" color="black" />
                    <ActionBtn title="Supprimer" icon="ti-trash" onClick={() => openDelete(t)} bg="var(--rd)" />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun type de frais trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouveau type de frais" : "Modifier le type de frais"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Code *</label>
                <input type="text" placeholder="Ex: SCOL" value={form.code} onChange={e => change('code', e.target.value.toUpperCase())} />
              </div>
              <div className="fg">
                <label>Période *</label>
                <select value={form.periode} onChange={e => change('periode', e.target.value)}>
                  {PERIODES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div className="fg">
              <label>Libellé *</label>
              <input type="text" placeholder="Ex: Frais de scolarité" value={form.libelle} onChange={e => change('libelle', e.target.value)} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.deductible_scolarite} onChange={e => change('deductible_scolarite', e.target.checked)} />
              Déductible de la scolarité
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.actif} onChange={e => change('actif', e.target.checked)} />
              Actif
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && selected && (
        <Modal title="Supprimer le type de frais" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Supprimer <strong>{selected.libelle}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>{saving ? "..." : "Supprimer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Types d'élèves ─────────────────────────────────────────── */
const EMPTY_TE = { code: "", libelle: "", ordre: 0, actif: true };

function TypesEleves({ slug }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_TE);

  const toast = useToastHook();
  const { getTypesEleves, createTypeEleve, updateTypeEleve, deleteTypeEleve } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getTypesEleves(slug));
    } catch (error) {
      toast.error("Impossible de charger les types d'élèves");
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(t => t.libelle?.toLowerCase().includes(search.toLowerCase()) || t.code?.toLowerCase().includes(search.toLowerCase()));

  const openCreate = () => { setForm(EMPTY_TE); setModal("create"); };
  const openEdit = (t) => { setSelected(t); setForm({ code: t.code, libelle: t.libelle, ordre: t.ordre, actif: t.actif }); setModal("edit"); };
  const openDelete = (t) => { setSelected(t); setModal("delete"); };
  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.code.trim() || !form.libelle.trim()) { toast.error("Le code et le libellé sont requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createTypeEleve(slug, form);
        setItems(prev => [...prev, created]);
        toast.success(`Type d'élève "${created.libelle}" créé avec succès`);
      } else {
        const updated = await updateTypeEleve(slug, selected.id, form);
        setItems(prev => prev.map(t => t.id === selected.id ? updated : t));
        toast.success(`Type d'élève "${updated.libelle}" modifié avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.code?.[0] || data?.libelle?.[0] || data?.non_field_errors?.[0] || "Une erreur est survenue";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteTypeEleve(slug, selected.id);
      setItems(prev => prev.filter(t => t.id !== selected.id));
      toast.success(`Type d'élève "${selected.libelle}" supprimé avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer ce type d'élève");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <input type="text" placeholder="Rechercher un type d'élève…" value={search} onChange={e => setSearch(e.target.value)} />
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouveau type d'élève</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr><th>Ordre</th><th>Code</th><th>Libellé</th><th>Statut</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {[...filtered].sort((a, b) => a.ordre - b.ordre).map(t => (
              <tr key={t.id}>
                <td>{t.ordre}</td>
                <td style={{ fontWeight: 500 }}>{t.code}</td>
                <td>{t.libelle}</td>
                <td><span className={`pill ${t.actif ? "ok" : "warn"}`}>{t.actif ? "Actif" : "Inactif"}</span></td>
                <td>
                  <div className="act">
                    <ActionBtn title="Modifier" icon="ti-edit" onClick={() => openEdit(t)} bg="#ffc107" color="black" />
                    <ActionBtn title="Supprimer" icon="ti-trash" onClick={() => openDelete(t)} bg="var(--rd)" />
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan="5" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun type d'élève trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouveau type d'élève" : "Modifier le type d'élève"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Code *</label>
                <input type="text" placeholder="Ex: ESH" value={form.code} onChange={e => change('code', e.target.value.toUpperCase())} />
              </div>
              <div className="fg">
                <label>Ordre d'affichage</label>
                <input type="number" value={form.ordre} onChange={e => change('ordre', Number(e.target.value))} />
              </div>
            </div>
            <div className="fg">
              <label>Libellé *</label>
              <input type="text" placeholder="Ex: Élève en situation de handicap" value={form.libelle} onChange={e => change('libelle', e.target.value)} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.actif} onChange={e => change('actif', e.target.checked)} />
              Actif
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && selected && (
        <Modal title="Supprimer le type d'élève" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Supprimer <strong>{selected.libelle}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>{saving ? "..." : "Supprimer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Tranches de paiement ───────────────────────────────────── */
const EMPTY_TR = { annee_scolaire: "", type_frais: "", code: "", libelle: "", date_echeance: "", ordre: 0 };

function Tranches({ slug }) {
  const [items, setItems] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [typesFrais, setTypesFrais] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [typeFraisFilt, setTypeFraisFilt] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_TR);

  const toast = useToastHook();
  const { getTranches, createTranche, updateTranche, deleteTranche, getAnneesScolaires, getTypesFrais } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);
  useEffect(() => { if (slug) load(); }, [slug, anneeFilt, typeFraisFilt]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, tfData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getTypesFrais(slug),
      ]);
      setAnnees(anneesData);
      setTypesFrais(tfData);
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const load = async () => {
    try {
      const params = {};
      if (anneeFilt) params.annee_scolaire = anneeFilt;
      if (typeFraisFilt) params.type_frais = typeFraisFilt;
      setItems(await getTranches(slug, params));
    } catch (error) {
      toast.error("Impossible de charger les tranches");
    }
  };

  const openCreate = () => { setForm({ ...EMPTY_TR, annee_scolaire: anneeFilt, type_frais: typeFraisFilt || "" }); setModal("create"); };
  const openEdit = (t) => { setSelected(t); setForm({ annee_scolaire: t.annee_scolaire, type_frais: t.type_frais, code: t.code, libelle: t.libelle, date_echeance: t.date_echeance, ordre: t.ordre }); setModal("edit"); };
  const openDelete = (t) => { setSelected(t); setModal("delete"); };
  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!form.annee_scolaire || !form.type_frais) { toast.error("L'année scolaire et le type de frais sont requis"); return; }
    if (!form.code.trim() || !form.libelle.trim() || !form.date_echeance) { toast.error("Le code, le libellé et la date d'échéance sont requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createTranche(slug, form);
        toast.success(`Tranche "${created.libelle}" créée avec succès`);
      } else {
        const updated = await updateTranche(slug, selected.id, form);
        toast.success(`Tranche "${updated.libelle}" modifiée avec succès`);
      }
      setModal(null);
      load();
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.non_field_errors?.[0] || data?.code?.[0] || data?.libelle?.[0] || data?.date_echeance?.[0] || "Une erreur est survenue";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await deleteTranche(slug, selected.id);
      setItems(prev => prev.filter(t => t.id !== selected.id));
      toast.success(`Tranche "${selected.libelle}" supprimée avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer cette tranche");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          <option value="">-- Toutes les années --</option>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <select value={typeFraisFilt} onChange={e => setTypeFraisFilt(e.target.value)}>
          <option value="">-- Tous les types de frais --</option>
          {typesFrais.map(tf => <option key={tf.id} value={tf.id}>{tf.libelle}</option>)}
        </select>
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        <button className="bprim" onClick={openCreate} disabled={typesFrais.length === 0}>
          <i className="ti ti-plus" />Nouvelle tranche
        </button>
      </div>

      {typesFrais.length === 0 && (
        <div className="alert-banner">
          <i className="ti ti-alert-triangle" />
          <span>Aucun type de frais configuré. Créez-en un dans l'onglet « Types de frais » avant d'ajouter des tranches.</span>
        </div>
      )}

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr><th>Type de frais</th><th>Code</th><th>Libellé</th><th>Échéance</th><th>Ordre</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {items.map(t => (
              <tr key={t.id}>
                <td>{t.type_frais_libelle}</td>
                <td style={{ fontWeight: 500 }}>{t.code}</td>
                <td>{t.libelle}</td>
                <td>{t.date_echeance}</td>
                <td>{t.ordre}</td>
                <td>
                  <div className="act">
                    <ActionBtn title="Modifier" icon="ti-edit" onClick={() => openEdit(t)} bg="#ffc107" color="black" />
                    <ActionBtn title="Supprimer" icon="ti-trash" onClick={() => openDelete(t)} bg="var(--rd)" />
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune tranche trouvée</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {(modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle tranche" : "Modifier la tranche"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Type de frais *</label>
              <select value={form.type_frais} onChange={e => change('type_frais', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {typesFrais.map(tf => <option key={tf.id} value={tf.id}>{tf.libelle}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Année scolaire *</label>
              <select value={form.annee_scolaire} onChange={e => change('annee_scolaire', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
            <div className="fgrid">
              <div className="fg">
                <label>Code *</label>
                <input type="text" placeholder="Ex: T1" value={form.code} onChange={e => change('code', e.target.value.toUpperCase())} />
              </div>
              <div className="fg">
                <label>Ordre</label>
                <input type="number" value={form.ordre} onChange={e => change('ordre', Number(e.target.value))} />
              </div>
            </div>
            <div className="fg">
              <label>Libellé *</label>
              <input type="text" placeholder="Ex: 1er versement" value={form.libelle} onChange={e => change('libelle', e.target.value)} />
            </div>
            <div className="fg">
              <label>Date d'échéance *</label>
              <input type="date" value={form.date_echeance} onChange={e => change('date_echeance', e.target.value)} />
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {modal === "delete" && selected && (
        <Modal title="Supprimer la tranche" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Supprimer <strong>{selected.libelle}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>{saving ? "..." : "Supprimer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ── Affectations de frais (classe × tranche × type d'élève) ─── */
function Affectations({ slug }) {
  const [items, setItems] = useState([]);
  const [classes, setClasses] = useState([]);
  const [tranches, setTranches] = useState([]);
  const [typesEleves, setTypesEleves] = useState([]);
  const [typeEleveFilt, setTypeEleveFilt] = useState(""); // un seul id sélectionné
  const [classeFilt, setClasseFilt] = useState(""); // "" = toutes les classes
  const [annees, setAnnees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [anneeFilt, setAnneeFilt] = useState("");
  const [modal, setModal] = useState(null);
  const [classeModal, setClasseModal] = useState(null);
  const [modalMulti, setModalMulti] = useState(false);

  const toast = useToastHook();
  const { getAffectationsFrais, getClasses, getTranches, getTypesEleves, getAnneesScolaires } = useAdminService();

  useEffect(() => { if (slug) init(); }, [slug]);
  useEffect(() => { if (slug && anneeFilt) loadClassesEtTranches(); }, [slug, anneeFilt]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, teData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getTypesEleves(slug),
      ]);
      setAnnees(anneesData);
      setTypesEleves(teData);
      // Par défaut, on affiche le premier type d'élève actif
      // (le plus courant, généralement "Normal") pour ne pas surcharger l'écran.
      const actifs = teData.filter(te => te.actif);
      if (actifs.length > 0) setTypeEleveFilt(String(actifs[0].id));
      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const loadClassesEtTranches = async () => {
    try {
      const [classesData, tranchesData] = await Promise.all([
        getClasses(slug, { annee_scolaire: anneeFilt, show_archived: "false" }),
        getTranches(slug, { annee_scolaire: anneeFilt }),
      ]);
      setClasses(classesData);
      setTranches(tranchesData);
      setClasseFilt(""); // réinitialise le filtre classe au changement d'année
      await load(classesData);
    } catch (error) {
      toast.error("Impossible de charger les classes et tranches");
    }
  };

  const load = async (classesData = classes) => {
    try {
      const all = await getAffectationsFrais(slug, {});
      const classeIds = new Set(classesData.map(c => c.id));
      setItems(all.filter(a => classeIds.has(a.classe)));
    } catch (error) {
      toast.error("Impossible de charger les affectations de frais");
    }
  };

  const typeEleveAffiche = typesEleves.find(te => String(te.id) === typeEleveFilt);

  // Une carte par classe ; les classes déjà configurées (pour le type
  // d'élève actuellement filtré) apparaissent en premier. Le filtre classe
  // restreint la liste à une seule classe si choisi.
  const classesAffichees = [...classes]
    .filter(c => !classeFilt || String(c.id) === classeFilt)
    .sort((a, b) => {
      const aConfig = items.some(i => i.classe === a.id && String(i.type_eleve) === typeEleveFilt) ? 0 : 1;
      const bConfig = items.some(i => i.classe === b.id && String(i.type_eleve) === typeEleveFilt) ? 0 : 1;
      return aConfig - bConfig || a.nom.localeCompare(b.nom);
    });

  const ouvrirModal = (classe) => { setClasseModal(classe); setModal("edit"); };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <select value={classeFilt} onChange={e => setClasseFilt(e.target.value)}>
          <option value="">-- Toutes les classes --</option>
          {classes.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
        <select value={typeEleveFilt} onChange={e => setTypeEleveFilt(e.target.value)}>
          {typesEleves.map(te => <option key={te.id} value={te.id}>{te.libelle}</option>)}
        </select>
        <button
          className="bprim"
          onClick={() => setModalMulti(true)}
          disabled={classes.length === 0}
          style={{ marginLeft: "auto" }}
        >
          <i className="ti ti-copy" /> Configurer plusieurs classes
        </button>
      </div>
      {tranches.length === 0 && (
        <div className="alert-banner">
          <i className="ti ti-alert-triangle" />
          <span>Aucune tranche configurée pour cette année. Créez-en dans l'onglet « Tranches » avant d'affecter des frais.</span>
        </div>
      )}

      {!typeEleveAffiche ? (
        <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
          Sélectionnez un type d'élève à afficher
        </div>
      ) : (
        <div className="affectations-grid">
          {classesAffichees.map(classe => (
            <ClasseAffectationCard
              key={classe.id}
              classe={classe}
              tranches={tranches}
              typeEleve={typeEleveAffiche}
              affectations={items.filter(a => a.classe === classe.id)}
              onEdit={() => ouvrirModal(classe)}
            />
          ))}
          {classesAffichees.length === 0 && (
            <div className="card" style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>
              Aucune classe trouvée pour ce filtre
            </div>
          )}
        </div>
      )}

      {modal === "edit" && classeModal && (
        <AffectationBulkModal
          slug={slug}
          classe={classeModal}
          tranches={tranches}
          typesElevesDisponibles={typesEleves}
          typeEleveInitial={typeEleveFilt}
          affectationsExistantes={items.filter(a => a.classe === classeModal.id)}
          onClose={() => { setModal(null); setClasseModal(null); }}
          onSaved={() => { setModal(null); setClasseModal(null); load(); }}
        />
      )}
      {modalMulti && (
        <AffectationBulkModal
          slug={slug}
          classes={classes}               // toutes les classes disponibles, à choisir dans le modal
          tranches={tranches}
          typesElevesDisponibles={typesEleves}
          typeEleveInitial={typeEleveFilt}
          affectationsExistantes={items}  // le modal filtrera lui-même par classe choisie
          onClose={() => setModalMulti(false)}
          onSaved={() => { setModalMulti(false); load(); }}
        />
      )}
      <style>{`
        .affectations-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 14px;
        }
      `}</style>
    </div>
  );
}

/* ── Carte résumé d'une classe : mini-tableau tranches × type d'élève ── */
function ClasseAffectationCard({ classe, tranches, typeEleve, affectations, onEdit }) {
  const configuree = affectations.some(a => a.type_eleve === typeEleve.id);
  const montant = (trancheId) =>
    affectations.find(a => a.tranche === trancheId && a.type_eleve === typeEleve.id)?.montant;

  const total = tranches.reduce((somme, t) => {
    const m = montant(t.id);
    return somme + (m !== undefined ? Number(m) : 0);
  }, 0);

  return (
    <div className="affc-card">
      <div className="affc-header">
        <div className="affc-titre">
          <i className="ti ti-school" />
          {classe.nom}
        </div>
        <ActionBtn
          title={configuree ? "Modifier" : "Configurer"}
          icon={configuree ? "ti-edit" : "ti-plus"}
          onClick={onEdit}
          bg={configuree ? "#ffc107" : "var(--pr)"}
          color={configuree ? "black" : "white"}
        />
      </div>

      {!configuree ? (
        <div className="affc-vide">
          <i className="ti ti-mood-empty" />
          Aucun frais affecté pour « {typeEleve.libelle} » dans cette classe
        </div>
      ) : (
        <>
          <div className="affc-tablewrap">
            <table className="affc-table">
              <thead>
                <tr><th>Tranche</th><th>{typeEleve.libelle}</th></tr>
              </thead>
              <tbody>
                {tranches.map(t => {
                  const m = montant(t.id);
                  return (
                    <tr key={t.id}>
                      <td className="affc-tranche">{t.libelle}</td>
                      <td>
                        {m !== undefined
                          ? <span className="affc-montant">{Number(m).toLocaleString()} <small>FCFA</small></span>
                          : <span className="affc-tiret">—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="affc-total">
            <span>Total à payer</span>
            <span className="affc-total-val">{total.toLocaleString()} FCFA</span>
          </div>
        </>
      )}

      <style>{`
        .affc-card {
          background: var(--s); border: 1px solid var(--b); border-radius: 12px;
          padding: 14px; display: flex; flex-direction: column; gap: 10px;
        }
        .affc-header {
          display: flex; align-items: center; justify-content: space-between; gap: 8px;
        }
        .affc-titre {
          display: flex; align-items: center; gap: 6px;
          font-weight: 700; font-size: 15px; color: var(--t);
        }
        .affc-titre i { font-size: 17px; color: var(--pr); }
        .affc-header .act, .affc-header button {
          width: 30px; height: 30px; border-radius: 4px; border: none; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          font-size: 14px; transition: all .2s;
        }
        .affc-vide {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: var(--h); padding: 10px 0;
        }
        .affc-tablewrap { overflow-x: auto; }
        .affc-table {
          width: 100%; border-collapse: collapse; font-size: 13px;
        }
        .affc-table th {
          text-align: left; font-weight: 600; color: var(--h);
          padding: 4px 8px; border-bottom: 1px solid var(--b);
          white-space: nowrap;
        }
        .affc-table td {
          padding: 6px 8px; border-bottom: 1px solid var(--b);
        }
        .affc-table tr:last-child td { border-bottom: none; }
        .affc-tranche { font-weight: 500; color: var(--t); white-space: nowrap; }
        .affc-montant { font-weight: 600; color: var(--t); }
        .affc-montant small { font-weight: 400; color: var(--h); }
        .affc-tiret { color: var(--h); }
        .affc-total {
          display: flex; align-items: center; justify-content: space-between;
          padding: 8px 10px; border-radius: 8px;
          background: var(--s2); border: 1px solid var(--b);
          font-size: 13px; font-weight: 600; color: var(--t);
        }
        .affc-total-val { color: var(--pr); font-size: 15px; }
      `}</style>
    </div>
  );
}

/* ── Modal de saisie groupée : tableau tranches × type d'élève choisi ── */
function AffectationBulkModal({ slug, classe, classes, tranches, typesElevesDisponibles, typeEleveInitial, affectationsExistantes, onClose, onSaved }) {
  // Mode "une classe" (depuis une carte) vs mode "plusieurs classes" (bouton groupé).
  const classesDisponibles = classes || (classe ? [classe] : []);
  const [classesChoisies, setClassesChoisies] = useState(classe ? [classe.id] : []);
  const [typeEleveChoisi, setTypeEleveChoisi] = useState(typeEleveInitial);
  const [valeurs, setValeurs] = useState(() => {
    // Pré-remplissage seulement pertinent en mode "une classe" ; en mode
    // multi, on part d'un tableau vide (les classes peuvent différer).
    if (!classe) return {};
    const init = {};
    affectationsExistantes
      .filter(a => a.classe === classe.id)
      .forEach(a => { init[`${a.tranche}-${a.type_eleve}`] = String(a.montant); });
    return init;
  });
  const [saving, setSaving] = useState(false);

  const toast = useToastHook();
  const { bulkAffectationsFrais } = useAdminService();

  const typeEleve = typesElevesDisponibles.find(te => String(te.id) === typeEleveChoisi);

  const toggleClasse = (id) => {
    setClassesChoisies(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const changer = (trancheId, value) => {
    setValeurs(prev => ({ ...prev, [`${trancheId}-${typeEleveChoisi}`]: value }));
  };

  const total = tranches.reduce((somme, t) => {
    const raw = valeurs[`${t.id}-${typeEleveChoisi}`];
    const n = Number(raw);
    return somme + (raw !== undefined && raw !== "" && !Number.isNaN(n) ? n : 0);
  }, 0);

  const handleSave = async () => {
    if (!typeEleve) { toast.error("Sélectionnez un type d'élève"); return; }
    if (classesChoisies.length === 0) { toast.error("Sélectionnez au moins une classe"); return; }

    const affectations = [];
    for (const t of tranches) {
      const raw = valeurs[`${t.id}-${typeEleveChoisi}`];
      if (raw === undefined || raw === "") continue;
      const montant = Number(raw);
      if (Number.isNaN(montant) || montant < 0) {
        toast.error(`Montant invalide pour ${t.libelle}`);
        return;
      }
      affectations.push({ tranche: t.id, type_eleve: typeEleve.id, montant });
    }
    if (affectations.length === 0) { toast.error("Renseignez au moins un montant"); return; }

    setSaving(true);
    try {
      await bulkAffectationsFrais(slug, { classes: classesChoisies, affectations });
      toast.success(
        classesChoisies.length > 1
          ? `Frais enregistrés pour ${classesChoisies.length} classes`
          : `Frais enregistrés avec succès`
      );
      onSaved();
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.affectations?.[0]?.non_field_errors?.[0] || data?.error || "Une erreur est survenue";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={classe ? `Frais de la classe ${classe.nom}` : "Configurer plusieurs classes"}
      onClose={onClose}
      width="min(560px, 95vw)"
    >
      {!classe && (
        <div className="fg" style={{ marginBottom: 10 }}>
          <label>Classes concernées *</label>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "8px 0" }}>
            {classesDisponibles.map(c => (
              <label key={c.id} style={{
                display: "flex", alignItems: "center", gap: 5,
                padding: "5px 10px", borderRadius: 999, border: "1px solid var(--b)",
                fontSize: 12, fontWeight: 500, cursor: "pointer",
                background: classesChoisies.includes(c.id) ? "var(--pr)" : "var(--s2)",
                color: classesChoisies.includes(c.id) ? "white" : "var(--m)",
              }}>
                <input type="checkbox" checked={classesChoisies.includes(c.id)} onChange={() => toggleClasse(c.id)} style={{ display: "none" }} />
                {c.nom}
              </label>
            ))}
          </div>
          <div style={{ fontSize: 12, color: "var(--h)" }}>
            Ex: CP1 à CM2, ou 6e à 3e — les mêmes montants seront appliqués à toutes les classes cochées.
          </div>
        </div>
      )}

      <div className="fg" style={{ marginBottom: 10 }}>
        <label>Type d'élève *</label>
        <select value={typeEleveChoisi} onChange={e => setTypeEleveChoisi(e.target.value)}>
          <option value="">-- Sélectionner --</option>
          {typesElevesDisponibles.map(te => <option key={te.id} value={te.id}>{te.libelle}</option>)}
        </select>
      </div>

      {tranches.length === 0 ? (
        <div style={{ fontSize: 14, color: "var(--h)" }}>Aucune tranche disponible pour cette année scolaire.</div>
      ) : !typeEleve ? (
        <div style={{ fontSize: 13, color: "var(--h)" }}>Sélectionnez un type d'élève ci-dessus.</div>
      ) : (
        <>
          <table className="tbl">
            <thead><tr><th>Tranche</th><th>Montant (FCFA)</th></tr></thead>
            <tbody>
              {tranches.map(t => (
                <tr key={t.id}>
                  <td style={{ fontWeight: 500 }}>{t.libelle}</td>
                  <td>
                    <input
                      type="number" min="0" step="0.01" placeholder="—"
                      style={{ width: 140 }}
                      value={valeurs[`${t.id}-${typeEleveChoisi}`] ?? ""}
                      onChange={e => changer(t.id, e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginTop: 10, padding: "8px 10px", borderRadius: 8,
            background: "var(--s2)", border: "1px solid var(--b)",
            fontSize: 13, fontWeight: 600, color: "var(--t)",
          }}>
            <span>Total scolarité à payer</span>
            <span style={{ color: "var(--pr)", fontSize: 15 }}>{total.toLocaleString()} FCFA</span>
          </div>
        </>
      )}

      <div className="factions">
        <button className="bsec" onClick={onClose} disabled={saving}>Annuler</button>
        <button className="bprim" onClick={handleSave} disabled={saving || tranches.length === 0 || !typeEleve || classesChoisies.length === 0}>
          {saving ? "En cours..." : <><i className="ti ti-check" /> Enregistrer{classesChoisies.length > 1 ? ` (${classesChoisies.length} classes)` : ""}</>}
        </button>
      </div>
    </Modal>
  );
}